import { Heading1 } from "@roc-digital/ui-web";
import { CommentInput } from "./CommentInput";
import { CommentList } from "./CommentList";
import { Comment } from "@roc-digital/types";
import { closeModal, openModal, useComments } from "@roc-digital/ui-lib";
import { useAuthState } from "@/logic";
import { useNavigate } from "react-router";
import { EditModal, ConfirmDeleteModal, SendReplyModal } from "./Modals";
import React from "react";


interface CommentsProps {
  refId?: string;
  commentId?: number;
  replyId?: number;
}

export function Comments(props: CommentsProps) {
  if(!props.refId) return null;

  return <ResetableContainer
    key={props.refId}
    commentId={props.commentId}
    replyId={props.replyId}
    refId={props.refId}/>
}

export function ResetableContainer(props: {refId: string, commentId?: number, replyId?: number}) {
  const comments = useComments(props.refId, undefined, props.commentId);
  const auth = useAuthState();
  const navigate = useNavigate();

  const onDelete = (comment: Comment) => {
    openModal(<ConfirmDeleteModal
      onCancel={closeModal}
      onConfirm={() => {
        closeModal();
        comments.delete(comment)
      }}
    />, 'small')
  }

  const onEdit = (comment: Comment) => {
    openModal(<EditModal
      body={comment.body}
      onCancel={closeModal}
      onDelete={() => onDelete(comment)}
      onSave={(body) => {
        closeModal();
        comments.edit(comment, body)
      }}
    />, 'small')
  }

  const RR = React.useMemo(() => {
      return (p: {comment: Comment, triggerNewReply?: number}) => {
        return RenderReplies({...p, replyId: props.replyId})
      }
  }, [props.replyId])

  return <div className="flex flex-col gap-4 mx-2">
    <Heading1 id="comments" className="dark:text-white mt-6 text-action !font-extrabold !text-[20px] !leading-9 !tracking-tight !font-chivo">
      Comments
    </Heading1>
    <CommentList
      comments={comments.comments}
      focusId={props.commentId}
      canPageDown={comments.canPageDown}
      canPageUp={comments.canPageUp}
      loading={comments.loading}
      loaded={comments.loaded}
      loadingUp={comments.loadingUp}
      loadingDown={comments.loadingDown}
      onPageDown={comments.pageDown}
      onPageUp={comments.pageUp}
      onEdit={onEdit}
      renderRepliesComponent={RR}
    />
    <CommentInput
      onLogin={(type) => {
        if(type === 'login') {
          navigate('/login')
        } else {
          navigate('/signup')
        }
      }}
      needsLogin={!auth.authenticated}
      onShare={comments.send}
    />
  </div>
}

function RenderReplies(props: {comment: Comment, triggerNewReply?: number, replyId?: number}) {
  const comments = useComments(props.comment.ref_id, props.comment.id, props.replyId);

  const onDelete = (comment: Comment) => {
    openModal(<ConfirmDeleteModal
      onCancel={closeModal}
      onConfirm={() => {
        closeModal();
        comments.delete(comment)
      }}
    />, 'small')
  }

  const onEdit = (comment: Comment) => {
    openModal(<EditModal
      body={comment.body}
      onCancel={closeModal}
      onDelete={() => onDelete(comment)}
      onSave={(body) => {
        closeModal();
        comments.edit(comment, body)
      }}
    />, 'small')
  }

  const onReply = (comment: Comment) => {
    openModal(<SendReplyModal
      comment={comment}
      onCancel={closeModal}
      onSend={(body) => {
        closeModal();
        comments.send(body)
      }}
    />, 'small')
  }

  React.useLayoutEffect(() => {
    if(props.triggerNewReply && !usedTriggers[props.triggerNewReply]) {
      usedTriggers[props.triggerNewReply] = true;
      onReply(props.comment)
    }
  }, [props.triggerNewReply])
  
  return <CommentList
    comments={comments.comments}
    focusId={props.replyId}
    canPageDown={comments.canPageDown}
    canPageUp={comments.canPageUp}
    loadingUp={comments.loadingUp}
    loadingDown={comments.loadingDown}
    loading={comments.loading}
    loaded={comments.loaded}
    onPageDown={comments.pageDown}
    onPageUp={comments.pageUp}
    onEdit={onEdit}
  />
}
const usedTriggers = {} as any;